import React, { FC, useCallback, useState } from 'react'
import classNames from 'classnames'
import AutoSizer from 'react-virtualized-auto-sizer'
import { FixedSizeList as List } from 'react-window'
import InfiniteLoader from 'react-window-infinite-loader'
import { useLegacyEffect } from '@/molecules/utils'
import { useMomentStoryModal } from '@/organisms/CollectibleSceneInfo/MomentStoryModal'
import { StoryCard } from '@/organisms/CollectibleSceneInfo/StoryCard'
import { useCollectiblesPlayerEngagement } from '@/organisms/CollectiblesPlayer/CollectiblesPlayerEngagement/CollectiblesPlayerEngagementContext'
import { MomentUserStory } from '@/services/MomentsService/types'
import { useUser } from '@/services/UserService'
import { Maybe, UserStory } from '@/types/codegen-federation'
import { useScreenType } from '@/utils/ScreenUtils'
import { useTranslate } from '@/utils/translate/translate-client'
import { getUserDisplayName } from '@/utils/users'

export interface StoriesListProps {
  className?: string
  userId?: Maybe<string>
}

const STORIES_PER_ROW = 3

export const StoryList: FC<StoriesListProps> = ({ className }) => {
  const { onOpen: onOpenModal } = useMomentStoryModal()
  const { t } = useTranslate('watch')
  const screenType = useScreenType()
  const { userId } = useUser()

  const { engagementMomentDetails, getMoreStories, isLoadingEngagementMomentDetails } =
    useCollectiblesPlayerEngagement()

  const [displayedStories, setDisplayedStories] = useState<{
    stories: UserStory[][]
    displayCount: number
    canFetchMore: boolean
  }>({
    stories: [],
    displayCount: 0,
    canFetchMore: false,
  })

  useLegacyEffect(() => {
    if (isLoadingEngagementMomentDetails) return

    const storiesRow = STORIES_PER_ROW
    const stories = engagementMomentDetails?.userStories.stories.filter(Boolean) as UserStory[]
    const videoStories = stories?.filter((story) => story?.videoUri !== null)
    const visibleStories = videoStories
    const totalStories = engagementMomentDetails?.userStories.totalStories || 0

    const storyChunks = visibleStories?.reduce((result, item, index) => {
      const chunkIndex = Math.floor(index / storiesRow)
      if (!result[chunkIndex]) {
        result[chunkIndex] = [] // start a new stories row
      }
      result[chunkIndex]?.push(item)

      return result
    }, new Array<Array<UserStory>>())

    setDisplayedStories((current) => {
      const displayCount = visibleStories.length + current.displayCount
      const canFetchMore = displayCount < totalStories

      return {
        stories: [...current.stories, ...storyChunks],
        displayCount,
        canFetchMore,
      }
    })
  }, [
    engagementMomentDetails?.userStories.stories,
    engagementMomentDetails?.userStories.totalStories,
    isLoadingEngagementMomentDetails,
  ])

  const handleOnOpen = useCallback(
    (story: NonNullable<MomentUserStory>) => {
      const storyToOpen = {
        id: story.id,
        userId: story.user?.id as string,
        text: story.text,
        videoUri: story.videoUri,
        isMyStory: Boolean(userId && story?.user?.id === userId),
        userAvatar: story?.user?.profile?.image,
        userName: getUserDisplayName(story?.user, t('anonymous', 'Anonymous')),
        publishDate: story?.publishDate,
        totalLikes: story?.totalLikes,
        isLikedByUser: story?.isLikedByUser,
        momentId: story?.momentId,
      }

      onOpenModal({
        ...storyToOpen,
        id: storyToOpen.id || undefined,
      })
    },
    [onOpenModal, t, userId],
  )

  const handleLoadMoreStories = useCallback(() => {
    if (isLoadingEngagementMomentDetails) return
    getMoreStories({ take: 30, skip: displayedStories.displayCount })
  }, [displayedStories.displayCount, getMoreStories, isLoadingEngagementMomentDetails])

  const COLUMN_WIDTH = screenType === 'desktop' ? 108 : 98
  const ROW_HEIGHT = 192

  const totalStoryRows = displayedStories.stories.length

  const isRowLoaded = (index: number) => {
    const canFetchMore = displayedStories.canFetchMore
    return index <= totalStoryRows - 2 || !canFetchMore
  }

  return (
    <div className={classNames('w-full h-full', className)}>
      <AutoSizer style={{ minHeight: '300px' }}>
        {({ width, height }: { width: number; height: number }) => (
          <InfiniteLoader
            itemCount={displayedStories.stories.length}
            loadMoreItems={handleLoadMoreStories}
            isItemLoaded={isRowLoaded}
            threshold={9}
          >
            {({ onItemsRendered, ref }) => (
              <List
                layout="vertical"
                className="scrollbar-thin scrollbar-track-core-gray-950 scrollbar-thumb-gray-800"
                itemSize={ROW_HEIGHT}
                itemCount={displayedStories.stories.length}
                onItemsRendered={onItemsRendered}
                height={height ?? '300px'}
                ref={ref}
                width={width}
              >
                {({ index, style }) => {
                  const rowGap = screenType === 'desktop' ? 12 : 8
                  return (
                    <div
                      style={{
                        ...style,
                        top: `${parseInt(style.top as string) + rowGap * index}px`,
                      }}
                      className="flex"
                    >
                      <div>
                        <div className="flex w-full items-start justify-start gap-x-2 xl:gap-x-3">
                          {displayedStories.stories[index]?.map(
                            (story) =>
                              story && (
                                <StoryCard
                                  style={{
                                    width: COLUMN_WIDTH,
                                    height: ROW_HEIGHT,
                                    maxWidth: COLUMN_WIDTH,
                                  }}
                                  key={story.id}
                                  story={story}
                                  onClick={() => handleOnOpen(story)}
                                  isInfiniteView={true}
                                />
                              ),
                          )}
                        </div>
                      </div>
                    </div>
                  )
                }}
              </List>
            )}
          </InfiniteLoader>
        )}
      </AutoSizer>
    </div>
  )
}
